import React, { useCallback } from "react";
import { Dialog, Portal } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { actions, selectors } from "@shared/store";
import { MODAL_TYPES } from "@shared/constants";
import { CreateCaseModal } from "@containers/Cases/components/CasesListContainer/CreateCaseModal";
import {
  CasePaymentErrorModal,
  UploadCaseModalSuccessModal,
  CasePaymentSuccessModal,
  DeleteCaseModal,
} from "@containers/Cases/components";
import MachineAnalysisPreviewModal from "@containers/Cases/components/CaseViewContainer/MachineAnalysis/MachineAnalysisPreviewModal/MachineAnalysisPreviewModal";
import { UploadMedicalRecordsModal } from "@containers/Cases/components/CaseViewContainer/CaseDocuments/UploadMedicalRecordsModal";
import { UploadCaseNotesModal } from "@containers/Cases/components/CaseViewContainer/CaseDocuments/UploadCaseNotesModal";
import { RemoveCaseDocumentModal } from "@containers/Cases/components/CaseViewContainer/CaseDocuments/RemoveCaseDocumentModal";
import { ChooseFieldValuesModal } from "@containers/Cases/components/CaseViewContainer/CaseDocuments/ChooseFieldValuesModal";

import InvalidCaseModal from "../../../containers/Cases/components/CasesListContainer/InvalidCaseModal/InvalidCaseModal";
import { ConfirmModal } from "./Modals";

const ModalWrapper = () => {
  const dispatch = useDispatch();
  const modal = useSelector(selectors.getModal());

  const initialRef = React.useRef(null);

  const hideModalAction = useCallback(() => {
    dispatch(actions.hideModal());
    if (modal?.props.onClose) {
      modal.props.onClose();
    }
  }, [dispatch, modal]);

  const onClose = useCallback(() => {
    if (modal?.leaveConfirm) {
      dispatch(
        actions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "You have unsaved changes",
            content: "Are you sure you want to leave? If you leave this page now your progress will be lost.",
            cancelBtnText: "Cancel",
            successBtnText: "Leave",
            onSuccess: hideModalAction,
          },
        }),
      );
      return;
    }

    hideModalAction();
  }, [modal, dispatch, hideModalAction]);

  const onSuccess = useCallback(
    (comment?: string) => {
      if (modal?.props.onSuccess) {
        modal.props.onSuccess(comment);
      }

      dispatch(actions.hideModal());
    },
    [modal, dispatch],
  );

  const Component = modal ? MODAL_COMPONENTS[modal.type as keyof typeof MODAL_COMPONENTS] : undefined;

  return modal ? (
    <Dialog.Root
      open={Boolean(modal)}
      onOpenChange={({ open }) => !open && onClose()}
      size={modal.size}
      placement="center"
      closeOnInteractOutside={modal.closeOnOverlayClick}
      initialFocusEl={() => initialRef.current}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content ref={initialRef} {...modal.modalContentProps}>
            {Component ? <Component {...modal.props} onClose={onClose} onSuccess={onSuccess} /> : null}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  ) : null;
};

export default ModalWrapper;

const MODAL_COMPONENTS = {
  [MODAL_TYPES.CONFIRM]: ConfirmModal,
  [MODAL_TYPES.CREATE_CASE]: CreateCaseModal,
  [MODAL_TYPES.UPLOAD_CASE_SUCCESS]: UploadCaseModalSuccessModal,
  [MODAL_TYPES.PAYMENT_SUCCESS]: CasePaymentSuccessModal,
  [MODAL_TYPES.PAYMENT_ERROR]: CasePaymentErrorModal,
  [MODAL_TYPES.DELETE_CASE]: DeleteCaseModal,
  [MODAL_TYPES.INVALID_DOCUMENT]: InvalidCaseModal,
  [MODAL_TYPES.PREVIEW_CASE]: MachineAnalysisPreviewModal,
  [MODAL_TYPES.UPLOAD_MEDICAL_RECORDS]: UploadMedicalRecordsModal,
  [MODAL_TYPES.UPLOAD_CASE_NOTES]: UploadCaseNotesModal,
  [MODAL_TYPES.REMOVE_CASE_DOCUMENT]: RemoveCaseDocumentModal,
  [MODAL_TYPES.CHOOSE_FIELD_VALUES]: ChooseFieldValuesModal,
};
